import { Dispatch, SetStateAction } from 'react';

import TranslateLangsMap from '@/utils/translate-langs-map.json';
import InitializeGreenroomProfileMask from '@/connection/greenroom/initialize-greenroom-profile-mask';
import SendGreenroomInvite from '@/connection/greenroom/send-greenroom-invite';
import { Channel } from '@/types/channel.types';
import { Meeting } from '@/types/greenroom/meeting.types';
import {
  GreenroomInviteUser,
  GreenroomInviteUserResponse,
  GreenroomUserProfile,
} from '@/types/greenroom/profile.types';
import { CreateSpeaker, ESpeakerInviteStatus, Speaker } from '@/types/speakers.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { useAppDispatch } from '@/store/hooks';
import { BLAdmin } from '@/types/profile.types';
import syncGreenroomInvites from '@/micros/edit-project/grid-areas/main/cast-and-crew/components/cast-content/helpers/sync-greenroom-invites';
import GreenroomButton from '@ui/greenroom-button/greenroom-button';
import Text from '@ui/text/text/text';
import { EToastTypes } from '@ui/toast/toast.types';
import { GetAdminContacts } from '@/connection/admins/get-admins';

type Props = {
  workingSpeaker: Speaker | CreateSpeaker;
  setWorkingSpeaker: Dispatch<SetStateAction<Speaker | CreateSpeaker>>;
  userChannel: Channel | null;
  meeting: Meeting | null;
  setInvitingCast: (inviting: boolean) => void;
  setGreenroomProfile: Dispatch<SetStateAction<GreenroomUserProfile | undefined>>;
  dispatch: ReturnType<typeof useAppDispatch>;
  setOpenInviteModal: Dispatch<SetStateAction<boolean>>;
  sessionUuid?: string;
  projectName?: string | null;
};

const handleSendGreenroomInvite = async (
  props: Props
): Promise<{ inviteResponse: GreenroomInviteUserResponse | null; admins: BLAdmin[] | null }> => {
  const {
    setInvitingCast,
    setOpenInviteModal,
    setWorkingSpeaker,
    setGreenroomProfile,
    userChannel,
    workingSpeaker,
    meeting,
    sessionUuid,
  } = props;
  if (!userChannel?.channel) {
    return { admins: null, inviteResponse: null };
  }

  if (!workingSpeaker.email) {
    toastTrigger('Email is required to send an invite', {
      type: EToastTypes.error,
      autoClose: 10000,
    });
    return { admins: null, inviteResponse: null };
  }
  if (!meeting?.meeting) {
    toastTrigger('Meeting information not found', {
      type: EToastTypes.warning,
      autoClose: false,
      toastId: 'missing-meeting-data',
      content: (
        <Text className="P2" design="GlobalIcoText3">
          You must enter{' '}
          <GreenroomButton
            tooltipProps={{ tooltipWrapperClass: 'display-inline-block' }}
            className="S3 padding-v-0 padding-h-4 radius-4"
            sessionUuid={sessionUuid}
          />{' '}
          to auto generate the necessary meeting data. After entering Greenroom® refresh the page and try again.
        </Text>
      ),
    });
    return { admins: null, inviteResponse: null };
  }

  setInvitingCast(true);

  try {
    const sendGreenroomInvitePostBody = createSendGreenroomInvitePostBody(props);

    if (!sendGreenroomInvitePostBody) throw new Error('Invalid user details');
    // create the invite and send it
    const sendInviteResponse = await SendGreenroomInvite(sendGreenroomInvitePostBody);

    // if the invite was sent, update the workingSpeaker
    setWorkingSpeaker((prev) => ({
      ...prev,
      invite_status: ESpeakerInviteStatus.pending,
    }));

    setGreenroomProfile(sendInviteResponse[0]?.profile);

    // initialize the profile mask
    await InitializeGreenroomProfileMask(meeting?.meeting, workingSpeaker.email);

    // refetch channel admins to retrieve latest since GR could've created a new admin if one doesn't exist for the email.
    const adminsResult = await GetAdminContacts({
      channel: userChannel.channel,
      includeLockedEmails: true,
      emails: [workingSpeaker.email],
    });

    const admins = Array.isArray(adminsResult) ? adminsResult : adminsResult?.admins || [];

    // if we're successful, send a socket message to GR to update GR redux state to re-render with latest invites
    const meetingId = meeting?.meeting;
    if (sendInviteResponse?.[0] && meetingId) {
      // no need to await since it's just a socket update to GR
      syncGreenroomInvites({ meetingId });
    }

    return { inviteResponse: sendInviteResponse?.[0] || null, admins };
  } catch (e) {
    console.error(e);
    toastTrigger('There was a problem inviting the cast member', {
      type: EToastTypes.error,
      autoClose: 5000,
    });
    return { admins: null, inviteResponse: null };
  } finally {
    setInvitingCast(false);
    setOpenInviteModal(false);
  }
};

export default handleSendGreenroomInvite;

const createSendGreenroomInvitePostBody = ({
  userChannel,
  workingSpeaker,
  meeting,
  projectName,
}: Props): GreenroomInviteUser | undefined => {
  if (!userChannel || !workingSpeaker.email) return;

  const greenroomLanguage = (TranslateLangsMap as Record<string, string>)[meeting?.language || 'en'];

  const invitation = {
    email: workingSpeaker.email,
    authentication: 'email_password',
    watcher_only: false,
  };

  return {
    channel: userChannel.channel,
    channel_name: userChannel.name,
    meeting: meeting?.meeting,
    title: projectName ? projectName : `${meeting?.title} - ${greenroomLanguage}`,
    invitations: [invitation],
  };
};
